import React, { useContext, memo, useState, useRef } from "react";

import {
	Space,
	Table,
	Select,	
	DatePicker,
	Button,
	Row,
	message,
	Tag,	
	Typography,
	theme,
	Popover,
	Tooltip,
	Divider,	
} from "antd";

const { Text } = Typography;

import {
	EyeOutlined,
	DollarOutlined,
	UndoOutlined,
	DownloadOutlined,
	PlusOutlined,
} from "@ant-design/icons";

import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import dayjs from "dayjs";
import PropTypes from "prop-types";

import { 
	formatCurrency,	
	dateWithTimeDefault,
	TextForTransactionState,
	formatToTwoDecimalPlaces,
	extractDepthClassifications,
} from "../../utils";

import { useCalculateComponentHeightSizeHook } from "../../../../hooks";
import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { BankAccountContext, EntitiesContext } from "../../../../contexts";
import { convertTimestampToDate } from "../../../../utils/Formaters";
import { DragHandle, EditableCell, EditableRowForTable } from "./EditableComponents";
import * as XLSX from "xlsx";
import { EditColumns } from "./EditColumns";
import { ModalActionSelected } from "./ModalsForBankTransactions";
import { ClassificationsContext } from "../../../../contexts/ClassificationsContext";

const TransactionState = Object.freeze({
	ARCHIVED: "archived",
	UNVERIFIED: "unverified",
	VERIFIED: "verified",
});

const ActionForTransactions = Object.freeze({
	EMPTY: "empty",
	ARCHIVE: "archive",
	RECOVER: "recover",
	DELETE: "delete",
	VERIFY: "verify",
});

const getStartingAmount = (firstMonthDetected, basePoints, initial_amount) => {
	const balance = basePoints[firstMonthDetected];
	
	if(!balance){
		return formatCurrency(initial_amount);
	}
	
	const dateForCheck = dateWithTimeDefault(firstMonthDetected);
	const month = dateForCheck.getMonth() + 1;
	const year = dateForCheck.getFullYear();
	
	const monthToRest = month - 1;
	const monthToCheck = monthToRest <= 0 ? 12 : monthToRest;
	const yearToCheck = monthToRest <= 0 ? year - 1 : year;
	
	const dateFormated = `${yearToCheck}-${String(monthToCheck).padStart(2, "0")}`;

	const balanceToCheck = basePoints[dateFormated];

	if(!balanceToCheck){
		return formatCurrency(initial_amount);		
	}

	return formatCurrency(balanceToCheck);	
};

export const TableBankTransactions = memo(({
	token,
	bankTransactionsData,
	backupBankTransactionsData,	
	sortedWithSaldo,
	editedTransactions,
	transactionsEditedProccessing,
	allBasePoints,	
	lastMonthsDetected,
	loading = false,
	sendingChanges = false,
	onBankTransactionsData = () => {},
	onBackupBankTransactionsData = () => {},
	rowClassName = () => {},
	updateBankTransactionData = () => {},
	onEditedTransactions = () => {},
	onSelectedTransaction = () => {},
	onSelectedTransactionForReconciliation = () => {},
	updateData = () => {},
	onOpenClassifications = () => {},
	children,
}) => {
	const modalActionSelectedRef = useRef(null);
	
	const { token : antToken } = theme.useToken();
	const { clients, suppliers } = useContext(EntitiesContext);		
	
	const { firstMonthDetected, lastMonthDetected } = lastMonthsDetected;	
	const { tableHeight } = useCalculateComponentHeightSizeHook({ heightDifference: 275 });	
	const { bankAccountSelected, updateBasePointsFromBankAccountSelected } = useContext(BankAccountContext);
	const { classifications } = useContext(ClassificationsContext);

	const [targetsSelected, setTargetsSelected] = useState([]);

	const checkTransactionLoading = (record, attribute) => {
		if (!transactionsEditedProccessing[record.id]) {
			return false;
		}

		return transactionsEditedProccessing[record.id][attribute] ? true : false;
	};
	
	const columns = [
		{
			key: "sort",
			dataIndex: "sort",
			align: "center",
			fixed: "left",
			width: 80,
			render: () => <DragHandle />,
		},
		{
			title: "Fecha",
			dataIndex: "date",
			width: 120,
			fixed: "left",
			align: "center",
			render: (_, record) => (
				<DatePicker
					size="small"
					style={{
						width: "100%",
						borderRadius: 5
					}}										
					status={rowClassName(record, "date") === "" ? "" : "warning"}
					value={dayjs(convertTimestampToDate(record.date))}
					onChange={(date) => {
						const formattedDate = date.format("YYYY-MM-DD HH:mm:ss");
						updateBankTransactionData("date", formattedDate, record);
					}}
					disabled={record?.state !== "unverified" || checkTransactionLoading(record, "date")}
				/>
			),
		},
		{
			title: "Referencia",
			dataIndex: "transaction_number",
			width: 120,
			fixed: "left",
			align: "center",			
			editable: true,
			rowClassName: rowClassName,
			cleanValue: (value) => value.replace(/\D/g, ""),
			disabled: (record) => record?.state !== "unverified" || checkTransactionLoading(record, "transaction_number"),
		},
		{
			title: "Descripción",
			dataIndex: "description",
			width: 350,
			align: "center",
			editable: true,
			rowClassName: rowClassName,
			ellipsis: {
				showTitle: false,
			},
			render: (value) => (
				<Tooltip placement="topLeft" title={value}> {value} </Tooltip>
			),
			disabled: (record) => record?.state !== "unverified" || checkTransactionLoading(record, "description"),
		},
		{
			title: "Crédito/Débito",
			dataIndex: "position",
			width: 115,
			align: "center",
			render: (_, record) => (
				<Select
					style={{
						width: "100%",
						border: 0,
						borderRadius: 5
					}}
					size="small"
					value={record?.position}
					status={rowClassName(record, "position") === "" ? "" : "warning"}
					onChange={(value) => {
						// Build the array of updates
						const updates = [{ key: "position", value }];
		
						// Conditional updates based on the new position
						if (value === "in" && record?.supplier_id) {
							updates.push({
								key: "supplier_id",
								value: null,
							});
							message.info("Proveedor removido, por favor asigna un Cliente");
						}
		
						if (value === "out" && record?.client_id) {
							updates.push({
								key: "client_id",
								value: null,
							});
							message.info("Cliente removido, por favor asigna un Proveedor");
						}
		
						// Call the custom batch update function
						updateMultipleBankTransactionData(updates, record);
					}}
					options={[
						{
							value: "in",
							label: "Crédito",
						},
						{
							value: "out",
							label: "Debito",
						}
					]}
					disabled={record?.state !== "unverified" || checkTransactionLoading(record, "position")}
				/>
			)
		},
		{
			title: "Monto",
			dataIndex: "amount",
			width: 130,
			align: "center",
			editable: true,
			inputType: "number",
			prefix: (record) => `${currencyGetter[bankAccountSelected?.currency]} ${record?.position === "in" ? "+" : "-"}`,
			rowClassName: rowClassName,
			disabled: (record) => record?.state !== "unverified" || checkTransactionLoading(record, "amount"),
		},							
		{
			title: "Saldo",
			dataIndex: "saldo",
			width: 120,
			align: "right",
			render: (_, record) => (
				<Text>{`${currencyGetter[bankAccountSelected?.currency]} ${formatCurrency(record.saldo)}`}</Text>
			)
		},
		{
			title: "Nota",
			dataIndex: "note",
			width: 300,
			align: "center",
			editable: true,
			rowClassName: rowClassName,
			ellipsis: {
				showTitle: false,
			},
			render: (value) => (
				<Tooltip placement="topLeft" title={value}> {value} </Tooltip>
			),
			disabled: (record) => record?.state !== "unverified" || checkTransactionLoading(record, "note"),
		},
		{
			title: "Clasificación",
			dataIndex: "classification_id",
			width: 300,
			align: "center",
			render: (_, record) => {											
				const options = extractDepthClassifications(classifications, 2, true);
			
				return (
					<Select
						showSearch
						allowClear
						placeholder="Selecciona clasificación"
						style={{ width: "100%" }}
						options={options}
						value={record.classification_id !== "" ? record.classification_id : null}
						onChange={(value) => updateBankTransactionData("classification_id", value ?? "", record)}
						disabled={record?.state !== "unverified" || checkTransactionLoading(record, "classification_id")}
						status={rowClassName(record, "classification_id") === "" ? "" : "warning"}
						optionFilterProp="label"
						dropdownRender={(menu) => (
							<>
								{menu}
								<Divider
									style={{
										margin: "8px 0",
									}}
								/>
								<Space
									style={{
										padding: "0 8px 4px",
										width: "100%",
										display: "flex",
										justifyContent: "center",
									}}
								>
									<Button
										type="text"
										icon={<PlusOutlined />}
										style={{ color: "#2d5db7" }}
										onClick={onOpenClassifications}
									>Agregar Nueva Clasificación</Button>
								</Space>
							</>
						)}
					/>
				);
			}
		},
		{
			title: "Entidad",
			dataIndex: "entity",
			width: 250,
			align: "center",
			render: (_, record) => (
				<>
					{record?.position === "in" && <Select
						style={{ width: "100%" }}
						value={record.client_id ?? null}
						status={rowClassName(record, "client_id") === "" ? "" : "warning"}
						onChange={(value) => {
							updateBankTransactionData("client_id", value, record);
						}}
						disabled={record?.state !== "unverified" || checkTransactionLoading(record, "client_id")}
					>
						<Select.Option value={null}>Cliente Vacio</Select.Option>
						{clients.map((client) => (
							<Select.Option key={client.id} value={client.id}>
								{client.name}
							</Select.Option>
						))}
					</Select>}
	
					{record?.position === "out" && <Select
						style={{ width: "100%" }}
						value={record.supplier_id === null ? null : record.supplier_id}
						status={rowClassName(record, "supplier_id") === "" ? "" : "warning"}
						onChange={(value) => {
							updateBankTransactionData("supplier_id", value, record);
						}}
						disabled={record?.state !== "unverified" || checkTransactionLoading(record, "supplier_id")}
					>
						<Select.Option value={null}>Proveedor Vacio</Select.Option>
						{suppliers.map((supplier) => (
							<Select.Option key={supplier.id} value={supplier.id}>
								{supplier.name}
							</Select.Option>
						))}
					</Select>}
				</>
			)
		},
		{
			title: "Monto de Referencia",
			dataIndex: "amont_original_reference",
			width: 150,
			align: "right",
			render: (_, record) => (									
				<Text style={{ color: "gray" }}>
					{`${currencyGetter[bankAccountSelected?.currency]} ${record.amount_original?.position === "in" ? "+" : "-"} ${formatCurrency(record.amount_original?.amount)}`}
				</Text>
			)
		},
		{
			title: "Consumido",
			dataIndex: "consumed",
			width: 90,
			align: "center",
			render: (_, record) => (
				<Text style={{ margin:0, color: record.state === "verified" ? "" : antToken.colorRed }}>
					{record.state === "verified" ? `${formatToTwoDecimalPlaces((record?.reconciliation_total / record?.amount) * 100)} %` : "No Aplica"}
				</Text>
			)
		},
		{
			title: "Saldo Referencia",
			dataIndex: "amount_reference",
			width: 125,
			fixed: "right",
			align: "right",
			render: (_, record) => {
				const isEqual = record.amount_reference && formatCurrency(record.amount_reference) === formatCurrency(record.saldo);
		
				return (
					<Text style={{ color: isEqual ? "green" : antToken.colorRed }}>
						{`${currencyGetter[bankAccountSelected?.currency]} ${record.amount_reference ? formatCurrency(record.amount_reference) : "N/A"}`}
					</Text>
				);
			}
		},
		{
			title: "Estado",
			dataIndex: "state",
			fixed: "right",
			width: 120,
			align: "center",
			filters: [
				{ value: TransactionState.UNVERIFIED, text: "No Verificado" },
				{ value: TransactionState.VERIFIED, text: "Verificado" },
				{ value: TransactionState.ARCHIVED, text: "Archivado" },
			],
			onFilter: (value, record) => record.state.indexOf(value) === 0,
			render: (_, record) => {
				const { color = "black", label = "N/A" } = TextForTransactionState[record?.state];
				return (
					<Tag color={color}>{label}</Tag>
				);
			}
		},
		{
			title: "Eventos",
			dataIndex: "action",
			fixed: "right",
			width: 130,
			align: "center",
			render: (_, record) => {
				return <Space>					
					<Popover placement="topLeft" title="Ver Más">
						<Button
							size="small"
							variant="outlined"
							onClick={() => onSelectedTransaction(record)}
						>
							<EyeOutlined />
						</Button>
					</Popover>

					<Popover placement="top" title="Reconciliación Bancaria">
						<Button
							variant="outlined"
							size="small" 
							disabled={record.state !== "verified"} 
							onClick={async() => onSelectedTransactionForReconciliation(record)}
						>
							<DollarOutlined />
						</Button>
					</Popover>

					<Popover placement="topRight" title="Restaurar">
						<Button
							size="small" 
							color="primary" 
							variant="outlined"
							onClick={() => restoreChanges(record.id)}
						>
							<UndoOutlined />
						</Button>
					</Popover>							
				</Space>;
			},
		},
	];
	
	// Custom Table Attributes
	const [activeColumns, setActiveColumns] = useState(columns.map((col) => col.dataIndex));

	const updateMultipleBankTransactionData = (updates, record) => {
		const { id } = record;
	
		const updatedTransactions = { ...editedTransactions };
	
		if (!updatedTransactions[id]) {
			updatedTransactions[id] = {};
		}
		
		updates.forEach(({ key, value }) => {
			updatedTransactions[id][key] = value;
		});
	
		onEditedTransactions(updatedTransactions);
	
		const updatedBankTransactionsData = bankTransactionsData.map((item) =>
			item.id === id
				? {
					...item,
					...updates.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}),
				}
				: item
		);
	
		onBankTransactionsData(updatedBankTransactionsData);
	};

	const restoreChanges = (transactionKey) => {
		onEditedTransactions(prevState => {
			const updatedTransactions = { ...prevState };
			delete updatedTransactions[transactionKey];
			return updatedTransactions;
		});

		onBankTransactionsData((prevState) => {
			const transactionToRestore = backupBankTransactionsData.find(
				(transaction) => transaction.id === transactionKey
			);

			if (!transactionToRestore) {
				console.warn(`Transaction with key ${transactionKey} not found in backup.`);
				return prevState;
			}

			const updatedTransactions = prevState.map((transaction) =>
				transaction.id === transactionKey ? transactionToRestore : transaction
			);

			return updatedTransactions;
		});
	};

	// DRAGABLE TABLE
	const sensors = useSensors(useSensor(PointerSensor));

	const onDragEnd = ({ active, over }) => {
		// Si no hubo cambio, no se procesa
		if (!over || active.id === over.id) {
			return;
		}
	
		// Reordenar los datos
		const oldIndex = sortedWithSaldo.findIndex(item => item.id === active.id);
		const newIndex = sortedWithSaldo.findIndex(item => item.id === over.id);
	
		const updatedTransactions = arrayMove(sortedWithSaldo, oldIndex, newIndex);
	
		// Agrupar las transacciones por fecha y asignar table_index dentro de cada grupo
		const updatedWithIndexes = [];
		const groupedByDate = {};
	
		// Agrupar por fecha
		updatedTransactions.forEach(transaction => {
			const dateKey = transaction.date.split(" ")[0];
			if (!groupedByDate[dateKey]) {
				groupedByDate[dateKey] = [];
			}
			groupedByDate[dateKey].push(transaction);
		});
	
		// Asignar los nuevos table_index dentro de cada grupo
		Object.values(groupedByDate).forEach(group => {
			group.forEach((transaction, index) => {
				updatedWithIndexes.push({
					...transaction,
					table_index: index,
				});
			});
		});
	
		// Detectar cambios en los table_index
		const editedTransactionUpdates = updatedWithIndexes.filter(updated => {
			const original = sortedWithSaldo.find(item => item.id === updated.id);
			const aDate = updated.date.split(" ")[0];
			const bDate = original.date.split(" ")[0];
			return !original || updated.table_index !== original.table_index || aDate !== bDate;
		});
	
		// Actualizar editedTransactions
		const updatedEditedTransactions = { ...editedTransactions };
	
		editedTransactionUpdates.forEach(({ id, table_index }) => {
			if (!updatedEditedTransactions[id]) {
				updatedEditedTransactions[id] = {};
			}
			updatedEditedTransactions[id]["table_index"] = table_index;
		});
	
		onEditedTransactions(updatedEditedTransactions);
	
		// Guardar cambios en los datos principales
		onBankTransactionsData(updatedWithIndexes);
	};

	const rowSelection = {
		columnWidth: 50,
		selectedRowKeys: targetsSelected,
		onChange: (newSelectedRowKeys) => {
			const previousKeys = targetsSelected;
	
			const addedKeys = newSelectedRowKeys.filter(key => !previousKeys.includes(key));
	
			const removedKeys = previousKeys.filter(key => !newSelectedRowKeys.includes(key));
	
			let updatedList = [...previousKeys];
	
			if (addedKeys.length > 0) {
				updatedList = [...updatedList, ...addedKeys];
			}
	
			if (removedKeys.length > 0) {
				updatedList = updatedList.filter(key => !removedKeys.includes(key));
			}
	
			setTargetsSelected(updatedList);
		},	
		getCheckboxProps: (record) => ({
			disabled: record?.state === "verified" || sendingChanges,
		}),
	};

	const tableComponents = {
		body: {
			row: EditableRowForTable,
			cell: EditableCell,
		},
	};

	const handleSave = (row, index) => {		
		updateBankTransactionData(index, row[index], row);		
	};

	const columnsToSee = columns.map((col) => {
		if (!col.editable) {
			return col;
		}

		return {
			...col,
			onCell: (record) => ({
				record,
				editable: col.editable,
				dataIndex: col.dataIndex,
				title: col.title,
				inputType: col.inputType || "text",
				rowClassName: col.rowClassName,
				prefix: typeof col.prefix === "function" ? col.prefix(record) : col.prefix,
				disabled: typeof col.disabled === "function" ? col.disabled(record) : col.disabled,
				cleanValue: col.cleanValue || null,
				handleSave: (row) => handleSave(row, col.dataIndex),
			}),
		};
	}).filter((col) => [...activeColumns].includes(col.dataIndex));
	
	const downloadExcel = () => {
		if (!sortedWithSaldo || sortedWithSaldo.length === 0) {
			message.error("No hay datos para exportar");
			return;
		}

		try {
			// Filter out archived transactions
			const filteredData = sortedWithSaldo.filter(row => row.state !== TransactionState.ARCHIVED);
			
			if (filteredData.length === 0) {
				message.warning("No hay transacciones para exportar");
				return;
			}
			
			// Get selected rows (excluding archived)
			const selectedRows = targetsSelected.length > 0
				? filteredData.filter(row => targetsSelected.includes(row.id))
				: filteredData;

			if (selectedRows.length === 0) {
				message.warning("No hay filas seleccionadas para exportar");
				return;
			}

			// Define which columns to include based on activeColumns
			const visibleColumns = columns.filter(col => activeColumns.includes(col.dataIndex) && col.title);
			
			// Format the data for Excel
			const formattedData = selectedRows.map(row => {
				const dataRow = {};
				
				visibleColumns.forEach(col => {
					// Skip action and sort columns
					if (col.dataIndex === "action" || col.dataIndex === "sort") return;
					
					// Special formatting for different column types
					if (col.dataIndex === "date") {
						dataRow[col.title] = convertTimestampToDate(row.date);
					} else if (col.dataIndex === "amount") {
						dataRow[col.title] = `${row.position === "in" ? "+" : "-"}${formatCurrency(row.amount)}`;
					} else if (col.dataIndex === "position") {
						dataRow[col.title] = row.position === "in" ? "Crédito" : "Débito";
					} else if (col.dataIndex === "saldo") {
						dataRow[col.title] = formatCurrency(row.saldo);
					} else if (col.dataIndex === "state") {
						dataRow[col.title] = TextForTransactionState[row.state]?.label || "";
					} else if (col.dataIndex === "entity") {
						const entity = row.position === "in" 
							? clients.find(c => c.id === row.client_id)?.name || "N/A"
							: suppliers.find(s => s.id === row.supplier_id)?.name || "N/A";
						dataRow[col.title] = entity;
					} else {
						dataRow[col.title] = row[col.dataIndex] || "";
					}
				});
				
				return dataRow;
			});

			// Create worksheet from data
			const worksheet = XLSX.utils.json_to_sheet(formattedData);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Transacciones");

			// Generate file name with date
			const fileName = `Transacciones_Bancarias_${dayjs().format("YYYY-MM-DD")}.xlsx`;
			
			// Save file
			XLSX.writeFile(workbook, fileName);
			
			message.success(`Archivo descargado: ${fileName}`);
		} catch (error) {
			console.error("Error al exportar a Excel:", error);
			message.error("Error al exportar a Excel");
		}
	};

	return (
		<DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
			<SortableContext
				items={sortedWithSaldo.map((i) => i.id)}
				strategy={verticalListSortingStrategy}
			>
				<Table
					rowKey="id"
					pagination={false}
					bordered
					size="small"
					columns={columnsToSee}
					dataSource={sortedWithSaldo}
					rowSelection={rowSelection}
					components={tableComponents}
					disabled={sendingChanges}
					title={() => (
						<Row justify="space-between">
							<Space>
								<td style={{paddingRight: "20px"}}>
									<Text>
										<strong>Seleccionados: </strong> {Object.keys(targetsSelected).length}
									</Text>
								</td>
								<Text>
									<strong>Transaciones Editadas: </strong> {Object.keys(editedTransactions).length}									
								</Text>									
							</Space>
							<Space>
								<ModalActionSelected
									ref={modalActionSelectedRef}
									token={token}
									bankAccountSelected={bankAccountSelected}
									bankTransactionsData={bankTransactionsData}
									backupBankTransactionsData={backupBankTransactionsData}
									onUpdateData={(updatedData) => {
										onBankTransactionsData(updatedData);
										onBackupBankTransactionsData(updatedData);
									}}									
									onUpdateBasePoints={(updatedBasePoints, updatedTemporalBasePoints) => updateBasePointsFromBankAccountSelected(updatedBasePoints, updatedTemporalBasePoints, updateData)}
									targetsSelected={targetsSelected}
								/>
								
								<Select			
									value={modalActionSelectedRef.current?.getModeSelected()}
									disabled={Object.keys(targetsSelected).length === 0}
									onChange={(value) => modalActionSelectedRef.current?.onSetModeSelected(value)}
									options={[
										{ value: ActionForTransactions.EMPTY, label: "Acciones" },
										{ value: ActionForTransactions.VERIFY, label: "Verificar" },
										{ value: ActionForTransactions.ARCHIVE, label: "Archivar" },
										{ value: ActionForTransactions.RECOVER, label: "Recuperar" },
										{ value: ActionForTransactions.DELETE, label: "Borrar" },
									]}
									style={{ width: "100px" }}
									className="custom-select"
								/>

								{children}

								<Tooltip title="Descargar">
									<Button
										type="primary"
										icon={<DownloadOutlined />}
										onClick={downloadExcel}
										loading={loading}
										ghost										
									>
									</Button>
								</ Tooltip>
								
								<EditColumns									
									columns={columns}
									activeColumns={activeColumns}
									loading={loading}
									onChangeColumns={setActiveColumns}
								/>
							</Space>
						</Row>
					)}
					footer={() => (
						<Row justify="end">			
							<td style={{paddingRight: "25%"}}>
								<Text>
									Transacciones del <strong>{firstMonthDetected}</strong> {lastMonthDetected !== "" ? <>al <strong>{lastMonthDetected}</strong></> : ""}
								</Text>
							</td>
											
							<td style={{paddingRight: "20px"}}>
								<Text>
									<strong>Saldo Inicial: </strong>
									{`${currencyGetter[bankAccountSelected?.currency]} ${getStartingAmount(firstMonthDetected, allBasePoints, bankAccountSelected.initial_amount)}`}
								</Text>								
							</td>

							<Text>
								<strong>Saldo Final: </strong>
								{`${currencyGetter[bankAccountSelected?.currency]} ${formatCurrency(sortedWithSaldo?.[sortedWithSaldo.length - 1]?.saldo || 0)}`}
							</Text>
						</Row>
					)}
					scroll={{
						x: "100%",
						y: tableHeight,
					}}
				/>
			</SortableContext>
		</DndContext>
	);
});

TableBankTransactions.displayName = "TableBankTransactions";

TableBankTransactions.propTypes = {
	token: PropTypes.string.isRequired,
	bankTransactionsData: PropTypes.array.isRequired,
	backupBankTransactionsData: PropTypes.array.isRequired,
	sortedWithSaldo: PropTypes.array.isRequired,
	editedTransactions: PropTypes.object.isRequired,
	transactionsEditedProccessing: PropTypes.object.isRequired,
	allBasePoints: PropTypes.object.isRequired,	
	lastMonthsDetected: PropTypes.shape({
		firstMonthDetected: "",
		lastMonthDetected: "",
	}),
	onBankTransactionsData: PropTypes.func,
	onBackupBankTransactionsData: PropTypes.func,
	rowClassName: PropTypes.func,
	onEditedTransactions: PropTypes.func,
	updateBankTransactionData: PropTypes.func,
	onSelectedTransaction: PropTypes.func,
	onSelectedTransactionForReconciliation: PropTypes.func,
	updateData: PropTypes.func,
	onOpenClassifications: PropTypes.func,
	children: PropTypes.node,
	sendingChanges: PropTypes.bool,
	loading: PropTypes.bool,
};